import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../App';
import api from '../services/api';
import { 
  Plus, 
  Settings2,
  DollarSign,
  Globe,
  Edit,
  Trash2,
  Loader2,
  Calculator,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const countries = [
  { code: 'UAE', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'India', name: 'India', flag: '🇮🇳' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'Singapore', name: 'Singapore', flag: '🇸🇬' },
  { code: 'Ghana', name: 'Ghana', flag: '🇬🇭' },
  { code: 'Kenya', name: 'Kenya', flag: '🇰🇪' },
  { code: 'Uganda', name: 'Uganda', flag: '🇺🇬' },
  { code: 'Cameroon', name: 'Cameroon', flag: '🇨🇲' },
  { code: 'Mauritius', name: 'Mauritius', flag: '🇲🇺' },
];

const componentTypes = [
  { value: 'earning', label: 'Earning', color: 'bg-emerald-500' },
  { value: 'deduction', label: 'Deduction', color: 'bg-red-500' },
  { value: 'employer_contribution', label: 'Employer Contribution', color: 'bg-blue-500' },
];

const calculationTypes = [
  { value: 'fixed', label: 'Fixed Amount' },
  { value: 'percentage_of_basic', label: '% of Basic' },
  { value: 'percentage_of_gross', label: '% of Gross' },
  { value: 'slab_based', label: 'Slab Based' },
  { value: 'custom', label: 'Custom Calculation' },
];

const SalaryConfig = () => {
  const { user } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState('UAE');
  const [components, setComponents] = useState([]);
  const [payrollConfig, setPayrollConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newComponent, setNewComponent] = useState({
    name: '',
    code: '',
    component_type: 'earning',
    calculation_type: 'fixed',
    value: 0,
    is_taxable: true,
    is_mandatory: false,
    min_value: null,
    max_value: null,
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    try {
      const [componentsRes, configRes] = await Promise.all([
        api.get('/salary-components', { params: { country: selectedCountry } }),
        api.get(`/payroll-configs/${selectedCountry}`).catch(() => ({ data: null }))
      ]);
      setComponents(componentsRes.data);
      setPayrollConfig(configRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateComponent = async () => {
    if (!newComponent.name || !newComponent.code) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingComponent) {
        await api.put(`/salary-components/${editingComponent.id}`, newComponent);
        toast.success('Component updated');
      } else {
        await api.post(`/salary-components?country=${selectedCountry}`, newComponent);
        toast.success('Component created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save component');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComponent = async (componentId) => {
    if (!confirm('Are you sure you want to deactivate this component?')) return;
    
    try {
      await api.delete(`/salary-components/${componentId}`);
      toast.success('Component deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete component');
    }
  };

  const openEditDialog = (component) => {
    setEditingComponent(component);
    setNewComponent({
      name: component.name,
      code: component.code,
      component_type: component.component_type,
      calculation_type: component.calculation_type,
      value: component.value,
      is_taxable: component.is_taxable,
      is_mandatory: component.is_mandatory,
      min_value: component.min_value,
      max_value: component.max_value,
      description: component.description || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingComponent(null);
    setNewComponent({
      name: '',
      code: '',
      component_type: 'earning',
      calculation_type: 'fixed',
      value: 0,
      is_taxable: true,
      is_mandatory: false,
      min_value: null,
      max_value: null,
      description: ''
    });
  };

  const getComponentTypeColor = (type) => {
    const found = componentTypes.find(t => t.value === type);
    return found?.color || 'bg-gray-500';
  };

  const getCountryFlag = (code) => {
    const country = countries.find(c => c.code === code);
    return country?.flag || '🌍';
  };

  const canEdit = ['super_admin', 'payroll_admin'].includes(user?.role);

  const earningComponents = components.filter(c => c.component_type === 'earning');
  const deductionComponents = components.filter(c => c.component_type === 'deduction');
  const employerComponents = components.filter(c => c.component_type === 'employer_contribution');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Salary Configuration</h1>
          <p className="text-muted-foreground">Configure country-specific salary components</p>
        </div>
        <div className="flex gap-4">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-56" data-testid="country-selector">
              <Globe size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countries.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="btn-hover" data-testid="add-component-btn">
                  <Plus size={18} className="mr-2" />
                  Add Component
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingComponent ? 'Edit' : 'Add'} Salary Component</DialogTitle>
                  <DialogDescription>
                    Configure a salary component for {selectedCountry}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Component Name</Label>
                      <Input
                        placeholder="House Rent Allowance"
                        value={newComponent.name}
                        onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })}
                        data-testid="component-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Code</Label>
                      <Input
                        placeholder="HRA"
                        value={newComponent.code}
                        onChange={(e) => setNewComponent({ ...newComponent, code: e.target.value.toUpperCase() })}
                        disabled={!!editingComponent}
                        data-testid="component-code"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Component Type</Label>
                      <Select 
                        value={newComponent.component_type} 
                        onValueChange={(v) => setNewComponent({ ...newComponent, component_type: v })}
                      >
                        <SelectTrigger data-testid="component-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {componentTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Calculation Type</Label>
                      <Select 
                        value={newComponent.calculation_type} 
                        onValueChange={(v) => setNewComponent({ ...newComponent, calculation_type: v })}
                      >
                        <SelectTrigger data-testid="calc-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {calculationTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>
                      {newComponent.calculation_type.includes('percentage') ? 'Percentage (%)' : 'Value'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={newComponent.value}
                      onChange={(e) => setNewComponent({ ...newComponent, value: parseFloat(e.target.value) || 0 })}
                      data-testid="component-value"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Min Value (Optional)</Label>
                      <Input
                        type="number"
                        placeholder="No minimum"
                        value={newComponent.min_value || ''}
                        onChange={(e) => setNewComponent({ ...newComponent, min_value: e.target.value ? parseFloat(e.target.value) : null })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Value (Optional)</Label>
                      <Input
                        type="number"
                        placeholder="No maximum"
                        value={newComponent.max_value || ''}
                        onChange={(e) => setNewComponent({ ...newComponent, max_value: e.target.value ? parseFloat(e.target.value) : null })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={newComponent.is_mandatory} 
                        onCheckedChange={(v) => setNewComponent({ ...newComponent, is_mandatory: v })}
                        data-testid="is-mandatory"
                      />
                      <Label>Mandatory Component</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={newComponent.is_taxable} 
                        onCheckedChange={(v) => setNewComponent({ ...newComponent, is_taxable: v })}
                        data-testid="is-taxable"
                      />
                      <Label>Taxable</Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description (Optional)</Label>
                    <Input
                      placeholder="Additional notes..."
                      value={newComponent.description}
                      onChange={(e) => setNewComponent({ ...newComponent, description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateComponent} disabled={submitting} data-testid="save-component-btn">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingComponent ? 'Update' : 'Create'} Component
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Country Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="text-4xl">{getCountryFlag(selectedCountry)}</div>
            <div>
              <h2 className="text-xl font-bold">{countries.find(c => c.code === selectedCountry)?.name}</h2>
              <p className="text-muted-foreground">
                {components.length} salary components configured
              </p>
            </div>
            {payrollConfig && (
              <div className="ml-auto flex gap-6">
                {payrollConfig.min_wage && (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Min Wage</p>
                    <p className="font-semibold">{payrollConfig.min_wage}</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Max Hours/Week</p>
                  <p className="font-semibold">{payrollConfig.max_working_hours_per_week}h</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">OT Multiplier</p>
                  <p className="font-semibold">{payrollConfig.overtime_multiplier}x</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Components Tabs */}
      <Tabs defaultValue="earnings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="earnings" data-testid="tab-earnings">
            <TrendingUp size={16} className="mr-2 text-emerald-500" />
            Earnings ({earningComponents.length})
          </TabsTrigger>
          <TabsTrigger value="deductions" data-testid="tab-deductions">
            <TrendingDown size={16} className="mr-2 text-red-500" />
            Deductions ({deductionComponents.length})
          </TabsTrigger>
          <TabsTrigger value="employer" data-testid="tab-employer">
            <DollarSign size={16} className="mr-2 text-blue-500" />
            Employer ({employerComponents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="earnings">
          <ComponentsTable 
            components={earningComponents} 
            canEdit={canEdit}
            onEdit={openEditDialog}
            onDelete={handleDeleteComponent}
          />
        </TabsContent>

        <TabsContent value="deductions">
          <ComponentsTable 
            components={deductionComponents} 
            canEdit={canEdit}
            onEdit={openEditDialog}
            onDelete={handleDeleteComponent}
          />
        </TabsContent>

        <TabsContent value="employer">
          <ComponentsTable 
            components={employerComponents} 
            canEdit={canEdit}
            onEdit={openEditDialog}
            onDelete={handleDeleteComponent}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ComponentsTable = ({ components, canEdit, onEdit, onDelete }) => {
  const getCalculationLabel = (type, value) => {
    switch (type) {
      case 'percentage_of_basic':
        return `${value}% of Basic`;
      case 'percentage_of_gross':
        return `${value}% of Gross`;
      case 'slab_based':
        return 'Slab Based';
      case 'custom':
        return 'Custom';
      default:
        return `Fixed: ${value}`;
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Component</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Calculation</TableHead>
              <TableHead>Limits</TableHead>
              <TableHead>Flags</TableHead>
              {canEdit && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {components.length > 0 ? (
              components.map((comp) => (
                <TableRow key={comp.id} data-testid={`component-row-${comp.code}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{comp.name}</p>
                      {comp.description && (
                        <p className="text-xs text-muted-foreground">{comp.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">{comp.code}</Badge>
                  </TableCell>
                  <TableCell>
                    {getCalculationLabel(comp.calculation_type, comp.value)}
                  </TableCell>
                  <TableCell>
                    {comp.min_value || comp.max_value ? (
                      <span className="text-sm">
                        {comp.min_value && `Min: ${comp.min_value}`}
                        {comp.min_value && comp.max_value && ' | '}
                        {comp.max_value && `Max: ${comp.max_value}`}
                      </span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {comp.is_mandatory && (
                        <Badge variant="secondary" className="text-xs">Mandatory</Badge>
                      )}
                      {comp.is_taxable && (
                        <Badge variant="outline" className="text-xs">Taxable</Badge>
                      )}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onEdit(comp)}
                          data-testid={`edit-${comp.code}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => onDelete(comp.id)}
                          data-testid={`delete-${comp.code}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-muted-foreground">
                  No components configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default SalaryConfig;
